import { create } from 'zustand';
import { User } from '@/types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string, refreshToken?: string | null) => void;
  clearAuth: () => void;
  updateUser: (user: User) => void;
  getRefreshToken: () => string | null;
  registerCleanup: (callback: () => void) => () => void;
}

// Cleanup callbacks invoked during clearAuth
const _cleanupCallbacks: Array<() => void> = [];

// Use sessionStorage instead of localStorage for better security
// sessionStorage is cleared when the browser tab is closed
// For persistent sessions, consider implementing secure httpOnly cookies via backend
const storage = sessionStorage;

const safeParse = (value: string | null) => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getInitialAuthState = () => {
  const user = safeParse(storage.getItem('user'));
  const token = storage.getItem('access_token');

  return {
    user,
    token,
    isAuthenticated: !!token,
  };
};

const initialState = getInitialAuthState();

export const useAuthStore = create<AuthState>((set, get) => ({
  user: initialState.user,
  token: initialState.token,
  isAuthenticated: initialState.isAuthenticated,

  setAuth: (user, token, refreshToken) => {
    // Store user data and tokens in sessionStorage only
    storage.setItem('user', JSON.stringify(user));
    storage.setItem('access_token', token);
    if (refreshToken) {
      storage.setItem('refresh_token', refreshToken);
    } else {
      storage.removeItem('refresh_token');
    }
    set({ user, token, isAuthenticated: true });
  },

  clearAuth: () => {
    // Clear all authentication data from sessionStorage only
    storage.removeItem('user');
    storage.removeItem('access_token');
    storage.removeItem('refresh_token');

    // Invoke all registered cleanup callbacks
    _cleanupCallbacks.forEach((cb) => {
      try { cb(); } catch { /* ignore cleanup errors */ }
    });

    set({ user: null, token: null, isAuthenticated: false });
  },

  updateUser: (user) => {
    storage.setItem('user', JSON.stringify(user));
    set({ user });
  },

  getRefreshToken: () => {
    return storage.getItem('refresh_token');
  },

  registerCleanup: (callback: () => void) => {
    _cleanupCallbacks.push(callback);
    // Return an unregister function
    return () => {
      const index = _cleanupCallbacks.indexOf(callback);
      if (index !== -1) {
        _cleanupCallbacks.splice(index, 1);
      }
    };
  },
}));
